import { useEffect, useState } from "react";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ExternalLink,
  Globe,
  Loader2,
  RotateCw,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { store, useStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import { ConnectPlatformCard, ViewLoading, relativeTime } from "@/views/shared";
import { ConfirmActionDialog } from "./ConfirmActionDialog";
import { DeploymentList } from "./DeploymentList";

function MonoLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex min-w-0 max-w-full items-center gap-1 font-mono text-xs text-primary hover:underline"
    >
      <span className="truncate">{label}</span>
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  );
}

type ConfirmKind = "redeploy" | "archive" | "delete";

export function ProjectDetailView({ id }: { id: string }) {
  const state = useStore();
  const detail = state.platform.projectDetail[id];
  const pendingAction = state.platform.pendingAction;
  const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);
  const [domainOpen, setDomainOpen] = useState(false);
  const [domain, setDomain] = useState("");

  useEffect(() => {
    void store.loadProjectDetail(id);
    void store.loadDeployments(id);
  }, [id]);

  if (!state.platform.available) return <ConnectPlatformCard />;

  const detailError = state.errors[`platform:detail:${id}`];
  // Mutations record failures under platform:<action>:<id>; surface them all
  // in one place so a failed dialog action is never silent after close.
  const actionErrors = (["redeploy", "archive", "delete", "domain"] as const)
    .map((k) => state.errors[`platform:${k}:${id}`])
    .filter((e): e is string => !!e);

  if (!detail) {
    return detailError ? (
      <div className="p-4">
        <p className="text-sm text-destructive">{detailError}</p>
      </div>
    ) : (
      <ViewLoading />
    );
  }

  const archived = detail.status === "archived";
  const domainPending = pendingAction === `domain:${id}`;
  const confirmPending =
    (confirmKind === "redeploy" && pendingAction === `redeploy:${id}`) ||
    (confirmKind === "archive" && pendingAction === `archive:${id}`) ||
    (confirmKind === "delete" && pendingAction === `delete:${id}`);

  async function runConfirm() {
    try {
      if (confirmKind === "redeploy") {
        await store.redeployProject(id);
      } else if (confirmKind === "archive") {
        await store.archivePlatformProject(id, archived ? "unarchive" : "archive");
      } else if (confirmKind === "delete") {
        await store.deletePlatformProject(id);
        setConfirmKind(null);
        store.navigate({ name: "projects" });
        return;
      }
    } catch {
      // Failure already lands in the errors map; close so it shows inline.
    }
    setConfirmKind(null);
  }

  async function saveDomain() {
    const value = domain.trim();
    if (!value) return;
    try {
      await store.setCustomDomain(id, value);
      setDomain("");
    } catch {
      // Error is shown inline via actionErrors.
    }
    setDomainOpen(false);
  }

  return (
    <div className="flex flex-col p-4">
      <div className="mb-3">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 h-7 gap-1 px-2 text-xs text-muted-foreground"
          onClick={() => store.navigate({ name: "projects" })}
        >
          <ArrowLeft className="h-3 w-3" />
          Projects
        </Button>
      </div>

      <header className="glass-border mb-4 rounded-xl p-4">
        <div className="flex items-center justify-between gap-2">
          <h1 className="truncate text-base font-semibold">{detail.name}</h1>
          <Badge
            variant="outline"
            className={cn(
              "shrink-0 font-mono text-[10px] uppercase tracking-wider",
              archived
                ? "border-border text-muted-foreground"
                : "border-primary/40 bg-primary/10 text-primary",
            )}
          >
            {detail.status ?? "unknown"}
          </Badge>
        </div>
        <div className="mt-2 flex flex-col gap-1">
          {detail.subdomain && (
            <span className="font-mono text-xs text-muted-foreground">{detail.subdomain}</span>
          )}
          {detail.productionUrl && (
            <MonoLink href={detail.productionUrl} label={detail.productionUrl.replace(/^https?:\/\//, "")} />
          )}
          {detail.customDomain && (
            <MonoLink
              href={`https://${detail.customDomain.replace(/^https?:\/\//, "")}`}
              label={detail.customDomain.replace(/^https?:\/\//, "")}
            />
          )}
          {detail.lastDeployAt && (
            <span className="text-xs text-muted-foreground">
              last deploy {relativeTime(detail.lastDeployAt)}
            </span>
          )}
        </div>
      </header>

      {detailError && <p className="mb-2 text-sm text-destructive">{detailError}</p>}
      {actionErrors.map((err) => (
        <p key={err} className="mb-2 text-sm text-destructive">{err}</p>
      ))}

      <div className="mb-6 flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={pendingAction === `redeploy:${id}`}
          onClick={() => setConfirmKind("redeploy")}
        >
          {pendingAction === `redeploy:${id}`
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <RotateCw className="h-4 w-4" />}
          Redeploy
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={pendingAction === `archive:${id}`}
          onClick={() => setConfirmKind("archive")}
        >
          {pendingAction === `archive:${id}`
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          {archived ? "Unarchive" : "Archive"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={domainPending}
          onClick={() => {
            setDomain(detail.customDomain ?? "");
            setDomainOpen(true);
          }}
        >
          {domainPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
          Domain
        </Button>
        <Button
          variant="destructive"
          size="sm"
          disabled={pendingAction === `delete:${id}`}
          onClick={() => setConfirmKind("delete")}
        >
          {pendingAction === `delete:${id}`
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Trash2 className="h-4 w-4" />}
          Delete
        </Button>
      </div>

      <DeploymentList projectId={id} />

      <ConfirmActionDialog
        open={confirmKind === "redeploy"}
        onOpenChange={(open) => { if (!open) setConfirmKind(null); }}
        title="Redeploy project"
        description={`Rebuild and publish ${detail.name} from its latest content.`}
        actionLabel="Redeploy"
        pending={confirmPending}
        onConfirm={runConfirm}
      />
      <ConfirmActionDialog
        open={confirmKind === "archive"}
        onOpenChange={(open) => { if (!open) setConfirmKind(null); }}
        title={archived ? "Unarchive project" : "Archive project"}
        description={
          archived
            ? `Restore ${detail.name} to the active project list.`
            : `Move ${detail.name} to the archive. It can be unarchived later.`
        }
        actionLabel={archived ? "Unarchive" : "Archive"}
        pending={confirmPending}
        onConfirm={runConfirm}
      />
      <ConfirmActionDialog
        open={confirmKind === "delete"}
        onOpenChange={(open) => { if (!open) setConfirmKind(null); }}
        title="Delete project"
        description={`Permanently delete ${detail.name} and its deployments. This cannot be undone.`}
        actionLabel="Delete project"
        destructive
        confirmName={detail.name}
        pending={confirmPending}
        onConfirm={runConfirm}
      />

      <Dialog open={domainOpen} onOpenChange={(open) => { if (!domainPending) setDomainOpen(open); }}>
        <DialogContent className="glass glass-border max-w-[calc(100vw-2rem)] rounded-xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Custom domain</DialogTitle>
            <DialogDescription>
              Point a domain you own at this project. DNS must already resolve to opzero.sh.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="custom-domain" className="text-xs text-muted-foreground">
              Domain
            </Label>
            <Input
              id="custom-domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
              disabled={domainPending}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={domainPending} onClick={() => setDomainOpen(false)}>
              Cancel
            </Button>
            <Button disabled={domainPending || !domain.trim()} onClick={() => void saveDomain()}>
              {domainPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
